import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { User, Bell, Moon, LogOut, Shield, Download, Megaphone, Users, Settings } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useMonthlyUsage } from '@/hooks/useMonthlyUsage';
import { UserAvatar } from '@/components/UserAvatar';
import { isAdminRole, isSystemAdminRole, isAgencyOwnerRole } from '@/lib/roles';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { isFeatureEnabled } from '@/config/featureFlags';
import { ACCOUNT_CENTER_FLAGS, ACCOUNT_HOME } from '@/lib/accountPaths';
import { SETTINGS_MODULE_FLAGS, SETTINGS_PATHS, SETTINGS_HOME_PAGE } from '@/lib/settingsPaths';
import { SupportContact } from '@/components/sidebar/SupportContact';

/**
 * Maps internal role slugs to human-readable labels
 */
function getRoleLabel(role: string | undefined | null): string {
  if (!role) return 'User';
  const normalized = role.trim().toUpperCase();
  switch (normalized) {
    case 'SYSTEM_ADMIN':
      return 'System Admin';
    case 'ADMIN':
      return 'Admin';
    case 'AGENCY_OWNER':
      return 'Agency Owner';
    case 'AGENCY_MANAGER':
      return 'Agency Manager';
    case 'ADVISOR':
      return 'Associate Advisor';
    case 'READ_ONLY':
      return 'Read Only';
    case 'USER':
    case 'PUBLIC_FREE_USER':
    default:
      return 'User';
  }
}

interface UserMenuProps {
  /** Whether the sidebar is expanded (affects menu positioning) */
  expanded: boolean;
  /** Optional callback when menu state changes */
  onMenuStateChange?: (open: boolean) => void;
  /** Optional callback to open legacy chat import modal */
  onImportLegacyChats?: () => void;
  /**
   * AR-210 (TC-41.1): rendered inside the phone More sheet. The panel then
   * opens in the sheet's flow instead of as a floating panel that the sheet's
   * scroll container clipped, which left Profile, Admin and Sign out unreachable.
   */
  inline?: boolean;
}

/**
 * UserMenu component for the sidebar bottom-left area.
 * Shows the member's PRO photo, else initials on their theme colour (AR-409,
 * via the shared UserAvatar / MemberAvatar).
 * Displays first+last name, role label, and monthly usage counter.
 * This is the single source of truth for user menu behavior (T-383).
 */
export const UserMenu: React.FC<UserMenuProps> = ({ expanded, onMenuStateChange, onImportLegacyChats, inline = false }) => {
  const [, navigate] = useLocation();
  const { user, logout } = useAuth();
  const { isDarkMode, setIsDarkMode } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isEnabled: isResolvedEnabled } = useResolvedFeatureFlags();

  // AR-291: profile nav items for features that aren't live yet stay hidden behind flags
  const showMonthlyUsage = isFeatureEnabled('profileMonthlyUsage');
  const showNotifications = isFeatureEnabled('profileNotifications');

  // AR-380: the Account center is opened from this menu, and replaces the
  // Profile and Team Management rows. While the migration flag is off, the two
  // old rows stay exactly as they are.
  // The same flag set the routes use — a menu row pointing at a gated-off
  // page is a dead end.
  const showAccountCenter = ACCOUNT_CENTER_FLAGS.every((f) => isResolvedEnabled(f));

  // AR-390: Settings is a module of its own (D-ACCT-28), opened from this menu
  // directly under Account. Its own flag pair, so it can ship after the
  // Account center rather than with it. On a phone this menu is the More
  // sheet's footer, which is how Account, Settings and Notifications reach
  // the handheld shell.
  const showSettingsModule = SETTINGS_MODULE_FLAGS.every((f) => isResolvedEnabled(f));

  // Fetch user profile for firstName/lastName
  const { firstName, lastName, email } = useUserProfile();

  // Fetch monthly usage stats - skipped entirely while the section is flagged off
  const { messageCount, limit, remaining } = useMonthlyUsage({ enabled: showMonthlyUsage });

  const roleLabel = getRoleLabel(user?.role);
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || user?.displayName || '';

  const handleMenuToggle = () => {
    const newState = !menuOpen;
    setMenuOpen(newState);
    onMenuStateChange?.(newState);
  };

  // AR-210 (TC-18.1-18.3, TC-25.3-25.4): menu keyboard pattern. Focus moves to
  // the first item on open; Up/Down/Home/End move between items; Escape closes
  // and returns focus to the trigger; Tab closes.
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuItems = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? []);
  const closeMenu = (returnFocus: boolean) => {
    setMenuOpen(false);
    onMenuStateChange?.(false);
    if (returnFocus) triggerRef.current?.focus();
  };
  useEffect(() => {
    if (!menuOpen) return;
    const raf = window.requestAnimationFrame(() => menuItems()[0]?.focus());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);
  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = menuItems();
    if (!items.length) return;
    const i = items.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (e.key === 'ArrowDown') next = (i + 1) % items.length;
    else if (e.key === 'ArrowUp') next = (i - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else if (e.key === 'Tab') {
      closeMenu(false);
      return;
    }
    if (next >= 0) {
      e.preventDefault();
      items[next].focus();
    }
  };

  const handleNavigate = (path: string) => {
    setMenuOpen(false);
    onMenuStateChange?.(false);
    navigate(path);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    onMenuStateChange?.(false);
    logout();
  };

  // Avatar button - different sizes for expanded/collapsed
  const avatarSize = expanded ? 'h-10 w-10' : 'h-8 w-8';
  const avatarPx = expanded ? 40 : 32;

  // Menu position based on sidebar state
  const menuPosition = inline
    ? 'relative mt-2 !w-full'
    : expanded
      ? 'absolute bottom-12 left-0'
      : 'fixed bottom-4 left-16';

  return (
    <div className="relative">
      {/* Avatar Button with optional Name/Role display */}
      <button
        ref={triggerRef}
        onClick={handleMenuToggle}
        className={`group flex items-center gap-3 ${expanded ? 'w-full rounded-lg px-2 py-2 hover:bg-gray-100 dark:hover:bg-[rgba(255,117,64,0.1)]' : `${avatarSize} justify-center overflow-hidden rounded-full hover:ring-2 hover:ring-orange-500`} transition-all`}
        title="User Menu"
        aria-label={fullName ? `User menu, ${fullName}` : 'User menu'}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        aria-controls={menuOpen ? menuId : undefined}
      >
        {/* Avatar — PRO photo, else initials on the member's theme colour (AR-409) */}
        <UserAvatar email={email} name={fullName} size={avatarPx} />
        
        {/* Name and Role - visible when expanded */}
        {expanded && (
          <div className="min-w-0 flex-1 text-left">
            <div className="truncate text-sm font-medium text-gray-900 dark:text-[#EAEAEA]">
              {fullName || 'User'}
            </div>
            <div className="text-xs text-gray-500 dark:text-[#9CA8A9]">{roleLabel}</div>
          </div>
        )}
        
        {/* Tooltip for collapsed state */}
        {!expanded && (
          <div className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded bg-gray-900 dark:bg-[#2C3536] px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 z-50">
            User Menu
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {menuOpen && (
        <>
          {/* Backdrop to close menu on outside click */}
          <div
            className="fixed inset-0 z-[200]"
            onClick={() => {
              setMenuOpen(false);
              onMenuStateChange?.(false);
            }}
          />
          <div
            className={`${menuPosition} z-[210] ${expanded ? 'w-72' : 'w-64'} rounded-lg border border-gray-200 dark:border-[rgba(255,117,64,0.15)] bg-white dark:bg-[#1A2021] py-2 shadow-lg`}
          >
            {/* User Info Section */}
            <div className="border-b border-gray-200 dark:border-[rgba(255,117,64,0.15)] px-4 py-3">
              <div className="flex items-center gap-3">
                {/* Small avatar in menu header — same component as the button */}
                <UserAvatar email={email} name={fullName} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-900 dark:text-[#EAEAEA]">
                    {fullName || 'User'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-[#9CA8A9]">{roleLabel}</div>
                </div>
              </div>
            </div>

            {/* Monthly Usage Section - gated by the profileMonthlyUsage flag (AR-291) */}
            {showMonthlyUsage && (
              <div className="border-b border-gray-200 dark:border-[rgba(255,117,64,0.15)] px-4 py-3">
                <div className="mb-1 text-xs text-gray-500 dark:text-[#9CA8A9]">Monthly Usage</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-[#EAEAEA]">{messageCount.toLocaleString()} / {limit.toLocaleString()}</div>
                <div className="text-xs text-gray-500 dark:text-[#9CA8A9]">
                  {remaining.toLocaleString()} interactions remaining
                </div>
              </div>
            )}

            {/* D-CHROME-CONTACT-01 / AR-408 — the support block: support line,
                live open/closed status, hours on demand, then the numbers a
                member quotes to book. Active members only: a lapsed member
                should not be handed a booking number they cannot use. */}
            {user?.isActive !== false && <SupportContact />}

            {/* Menu Items — AR-210: the role=menu container holds only the items
                (the user info and contact blocks above are plain content). */}
            <div role="menu" id={menuId} aria-label="User menu" ref={menuRef} onKeyDown={handleMenuKeyDown}>
            {showAccountCenter ? (
              <button
                className="flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-[#EAEAEA] hover:bg-gray-50 dark:hover:bg-[rgba(255,117,64,0.1)]"
                onClick={() => handleNavigate(ACCOUNT_HOME)}
                role="menuitem"
                tabIndex={-1}
              >
                <User className="h-4 w-4" />
                <span>Account</span>
              </button>
            ) : (
              <button
                className="flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-[#EAEAEA] hover:bg-gray-50 dark:hover:bg-[rgba(255,117,64,0.1)]"
                onClick={() => handleNavigate('/profile')}
                role="menuitem"
                tabIndex={-1}
              >
                <User className="h-4 w-4" />
                <span>Profile</span>
              </button>
            )}
            {showSettingsModule && (
              <button
                className="flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-[#EAEAEA] hover:bg-gray-50 dark:hover:bg-[rgba(255,117,64,0.1)]"
                onClick={() => handleNavigate(SETTINGS_PATHS[SETTINGS_HOME_PAGE])}
                role="menuitem"
                tabIndex={-1}
              >
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </button>
            )}
            {/* Notifications - gated by the profileNotifications flag (AR-291) */}
            {showNotifications && (
              <button
                className="flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-[#EAEAEA] hover:bg-gray-50 dark:hover:bg-[rgba(255,117,64,0.1)]"
                role="menuitem"
                tabIndex={-1}
              >
                <Bell className="h-4 w-4" />
                <span>Notifications</span>
              </button>
            )}

            {/* What's New - product update history for non-system-admin users */}
            {!isSystemAdminRole(user?.role) && (
              <button
                className="flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-[#EAEAEA] hover:bg-gray-50 dark:hover:bg-[rgba(255,117,64,0.1)]"
                onClick={() => handleNavigate('/product-updates')}
                role="menuitem"
                tabIndex={-1}
              >
                <Megaphone className="h-4 w-4" />
                <span>What's New</span>
              </button>
            )}

            {/* Import Legacy Chats - always visible for users who might have old chats */}
            {onImportLegacyChats && (
              <button
                className="flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-[#EAEAEA] hover:bg-gray-50 dark:hover:bg-[rgba(255,117,64,0.1)]"
                onClick={() => {
                  setMenuOpen(false);
                  onMenuStateChange?.(false);
                  onImportLegacyChats();
                }}
                role="menuitem"
                tabIndex={-1}
              >
                <Download className="h-4 w-4" />
                <span>Import Legacy Chats</span>
              </button>
            )}

            {/* Team Management - only visible to Agency Owner and admins, gated by
                invoicingMvp. AR-380: the Account center's Team + Access page
                replaces it, so the row drops once the migration flag is on. */}
            {!showAccountCenter && isResolvedEnabled('invoicingMvp') && (isAgencyOwnerRole(user?.role) || isAdminRole(user?.role)) && (
              <button
                className="flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-[#EAEAEA] hover:bg-gray-50 dark:hover:bg-[rgba(255,117,64,0.1)]"
                onClick={() => handleNavigate('/settings/team')}
                role="menuitem"
                tabIndex={-1}
              >
                <Users className="h-4 w-4" />
                <span>Team Management</span>
              </button>
            )}

            {/* Admin Link - only visible to admin roles */}
            {/* SYSTEM_ADMIN goes to /admin, basic ADMIN goes to /admin/users */}
            {isAdminRole(user?.role) && (
              <button
                className="flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-[#EAEAEA] hover:bg-gray-50 dark:hover:bg-[rgba(255,117,64,0.1)]"
                onClick={() => handleNavigate(isSystemAdminRole(user?.role) ? '/admin' : '/admin/users')}
                role="menuitem"
                tabIndex={-1}
              >
                <Shield className="h-4 w-4" />
                <span>Admin</span>
              </button>
            )}

            {/* Dark Mode Toggle */}
            <div className="flex w-full items-center justify-between px-4 py-2">
              <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-[#EAEAEA]">
                <Moon className="h-4 w-4" />
                <span>Dark Mode</span>
              </div>
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                role="menuitemcheckbox"
                aria-checked={isDarkMode}
                tabIndex={-1}
                aria-label="Dark mode"
                className={`${isDarkMode ? 'bg-[#fb923c]' : 'bg-gray-200'} relative inline-flex h-5 w-9 items-center rounded-full transition-colors`}
              >
                <span className={`${isDarkMode ? 'translate-x-5' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
              </button>
            </div>

            {/* Sign Out */}
            <button
              onClick={handleLogout}
              className="flex w-full items-center space-x-2 border-t border-gray-200 dark:border-[rgba(255,117,64,0.15)] px-4 py-2 text-left text-sm text-red-600 dark:text-[#FF9E7A] hover:bg-gray-50 dark:hover:bg-[rgba(255,117,64,0.1)]"
              role="menuitem"
                tabIndex={-1}
            >
              <LogOut className="h-4 w-4" />
              <span>Sign out</span>
            </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserMenu;
